import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { SubmitErrorHandler, SubmitHandler } from "react-hook-form";
import z from "zod";
import useAppDispatch from "@/hooks/useAppDispatch";
import wooStoreApiService from "@/api/services/wooStoreApiService";
import { handleApiError } from "@/helpers/apiErrorHelper";
import { transformToEnumValue } from "@/utils/utils";
import { PaymentMethodId } from "@/constants/enums";
import useCart from "@/features/cart/useCart";
import useCheckout from "./features/useCheckout";
import useShippingMethods from "./features/useShippingMethods";
import usePaymentMethods from "./features/usePaymentMethods";
import type { PaymentGateway } from "@/types/payments";
import useCheckoutForm, {
  type CheckoutForm,
  type FormSchemaType,
} from "./features/useCheckoutForm";
import useCVSStore, {
  type CvsRedirectPayload,
  type CvsSelectionReturn,
} from "./features/useCVSStore";
import type { CvsShippingMethodId } from "@/types/shipping";
import { CvsTypeName, ShippingMethodId, ShippingMethodType } from "@/constants/enums";
import {
  CheckoutReturnAction,
  CheckoutReturnActionStatus,
  type CVSShippingFields,
  type OrderData,
} from "@/types/checkout";
import type {
  SubmitOrderForCVSShippingRequestPayload,
  SubmitOrderForPostShippingRequestPayload,
} from "@/types/api/wooStoreApi";

//#region types
interface UseCheckoutPageReturn {
  cart: ReturnType<typeof useCart>["cart"];

  isShippingMethodsLoading: ReturnType<typeof useShippingMethods>["isShippingMethodsLoading"];
  shippingOptions: ReturnType<typeof useShippingMethods>["shippingOptions"];

  control: ReturnType<typeof useCheckoutForm>["control"];
  errors: ReturnType<typeof useCheckoutForm>["errors"];
  register: ReturnType<typeof useCheckoutForm>["register"];
  watch: ReturnType<typeof useCheckoutForm>["watch"];
  shippingMethodIdField: ReturnType<typeof useCheckoutForm>["shippingMethodIdField"];

  cvsStore: ReturnType<typeof useCVSStore>["cvsStore"];
  cvsMapFormWrapRef: ReturnType<typeof useCVSStore>["cvsMapFormWrapRef"];
  queryCVSMap: ReturnType<typeof useCVSStore>["queryCVSMap"];

  isPageInitialized: boolean;
  shippingMethodType: ShippingMethodType;
  cvsTypeName: CvsTypeName;
  allowedPaymentMethods: PaymentGateway[];
  isOrderSubmitting: boolean;
  handleBeforeRedirect: (runRedirectAction: () => void) => Promise<void>;
  handleFormSubmit: () => void;
}
//#endregion types

const useCheckoutPage = (): UseCheckoutPageReturn => {
  const appDispatch = useAppDispatch();

  const { cart, clearCart, canCheckoutCart } = useCart();

  const {
    isShippingMethodsLoading,
    shippingOptions,
    getShippingMethodType,
    updateSelectedShippingMethod,
  } = useShippingMethods({ cart });

  const { paymentMethods, isPaymentMethodsLoading, getAllowedPaymentMethods } = usePaymentMethods();

  const {
    register,
    control,
    errors,
    setValue,
    setError,
    getValues,
    watch,
    reset,
    handleSubmit,
    shippingMethodIdField,
  } = useCheckoutForm();

  const { submitCheckoutOrder } = useCheckout();

  const cvsRedirectPayload: CvsRedirectPayload = {
    shippingMethodId: shippingMethodIdField,
    paymentMethodId: getValues().paymentMethodId,
  };

  const {
    cvsStore,
    cvsMapFormWrapRef,
    cvsSelectionReturn,
    isCVSStoreLoading,
    setCVSStore,
    queryCVSMap,
    getCvsTypeName,
    handleCVSSelectionError,
    resetCVSStore,
  } = useCVSStore({
    cvsRedirectPayload, // 離站需要將表單存到 api 返回時帶回來
  });

  const navigate = useNavigate();

  const [isPageInitialized, setIsPageInitialized] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const shouldHandleShippingChange = useRef(true); // 用於避免初始設定運送方式時觸發異動

  const shippingMethodType = useMemo(
    () => getShippingMethodType(shippingMethodIdField),
    [shippingMethodIdField],
  );

  const cvsTypeName = useMemo(
    () => getCvsTypeName(shippingMethodIdField as CvsShippingMethodId),
    [shippingMethodIdField],
  );

  const allowedPaymentMethods = useMemo(
    () => (isPageInitialized ? getAllowedPaymentMethods(shippingMethodIdField) : []),
    [shippingMethodIdField, isPageInitialized],
  );

  const initializePage = () => {
    if (cart && shippingOptions && paymentMethods) {
      setIsPageInitialized(true);
    }
  };

  const validateCartAndOrder = () => {
    if (cart?.items.length === 0) {
      navigate("/cart", { replace: true });
    }
  };

  const handleShippingMethodChange = async () => {
    if (!shouldHandleShippingChange.current) {
      shouldHandleShippingChange.current = true;
      return;
    }
    await updateSelectedShippingMethod(shippingMethodIdField); // TODO: 快速切換時中止前一個請求
    resetCVSStore(); // 更換運送方式時，移除已選的超商
    setValue("cvsStoreId", "");
    setValue("paymentMethodId", undefined); // 付款選項隨運送方式更新，重置讓使用者重選
  };

  // 跳轉第三方前保存使用者已填的結帳資料到後端資料庫
  const handleBeforeRedirect = async (runRedirectAction: () => void) => {
    appDispatch.setPageLoading(true);
    const form = getValues();

    try {
      await wooStoreApiService.cart.updateAddress({
        shipping_address: form.shippingAddress,
        billing_address: { email: form.email },
      });
      runRedirectAction();
    } catch (error) {
      handleApiError(
        error,
        appDispatch,
        "更新結帳資料發生問題，請確認結帳資料是否正確，並重新再試一次",
        {
          autoClose: false,
        },
      );
    } finally {
      appDispatch.setPageLoading(false);
    }
  };

  // 還原使用者跳轉站外前已填的資料
  const processCvsSelectionResult = (cvsSelectionReturn: CvsSelectionReturn) => {
    if (!cvsSelectionReturn) return;
    if (cvsSelectionReturn.action === CheckoutReturnAction.CVS_STORE_SELECTION) {
      reset({
        email: cart?.billing_address.email,
        shippingAddress: cart?.shipping_address,
        shippingMethodId: transformToEnumValue(
          ShippingMethodId,
          cvsSelectionReturn.shippingMethodId,
        ),
      });
    }

    if (cvsSelectionReturn?.status === CheckoutReturnActionStatus.ERROR) {
      appDispatch.pushAlert({
        type: "error",
        title: handleCVSSelectionError(cvsSelectionReturn.errorCode ?? ""),
      });
    }

    if (cvsSelectionReturn.status === CheckoutReturnActionStatus.SUCCESS) {
      setCVSStore({
        CVSStoreID: cvsSelectionReturn.cvsStoreId ?? "",
        CVSStoreName: cvsSelectionReturn.cvsStoreName ?? "",
        CVSAddress: cvsSelectionReturn.cvsStoreAddress ?? "",
      });
      setValue("cvsStoreId", cvsSelectionReturn.cvsStoreId ?? "");
    }

    shouldHandleShippingChange.current = false; // 初始設定運送方式時，避免觸發異動
  };

  //#region submit order
  const getValidateCVSShippingFields = (form: FormSchemaType): CVSShippingFields | {} => {
    if (shippingMethodType !== ShippingMethodType.CVS) return {};

    const cvsShippingFieldsSchema = z.object({
      receiver_name: z.string().nonempty("收件人為必填"),
      receiver_email: z.string().email("Email 格式錯誤").nonempty("未提供 Email"),
      receiver_cellphone: z
        .string()
        .regex(/^09\d{8}$/, "手機號碼格式不正確，請填寫例如 0912xxx999")
        .nonempty("未提供手機號碼"),
      receiver_store_id: z.string().nonempty("未提供超商門市號碼"),
      receiver_store_name: z.string().nonempty("未提供超商門市名稱"),
      receiver_store_address: z.string().nonempty("未提供超商門市地址"),
    });

    const validationResult = cvsShippingFieldsSchema.safeParse({
      receiver_name: form.shippingAddress.last_name + form.shippingAddress.first_name,
      receiver_cellphone: form.shippingAddress.phone,
      receiver_email: form.email,
      receiver_store_id: cvsStore?.CVSStoreID ?? "",
      receiver_store_name: cvsStore?.CVSStoreName ?? "",
      receiver_store_address: cvsStore?.CVSAddress ?? "",
    });

    if (validationResult.success) {
      return validationResult.data;
    } else {
      validationResult.error.issues.forEach((issue) => {
        const fieldName = issue.path[0] as keyof FormSchemaType;
        setError(fieldName, { type: "manual", message: issue.message });
      });
      throw new Error("超商取貨資料驗證失敗，請確認結帳資料正確後再送出");
    }
  };

  const validateCart = async () => {
    const canCheckout = await canCheckoutCart();
    if (!canCheckout) {
      throw new Error(
        "訂單中的商品有問題，可能是缺貨或其他錯誤，請返回購物車頁面檢查並重新操作一次。",
      );
    }
  };

  const preSubmit = async (
    formData: FormSchemaType,
  ): Promise<
    SubmitOrderForCVSShippingRequestPayload | SubmitOrderForPostShippingRequestPayload
  > => {
    let payload;

    try {
      const cvsShippingFields = getValidateCVSShippingFields(formData);

      payload = {
        shipping_address: formData.shippingAddress,
        billing_address: { ...formData.shippingAddress, email: formData.email },
        payment_method: formData.paymentMethodId as PaymentMethodId,
        customer_note: formData.customNote,

        // 自訂的 ExtraOrderFields
        shipping_method_id: formData.shippingMethodId,
        total_amount: cart?.totals.total_price ?? "0",

        ...(shippingMethodType == ShippingMethodType.CVS ? cvsShippingFields : {}),
      };
    } catch (error) {
      throw error;
    }

    return payload;
  };

  const navigateToOrderPay = (order: OrderData) => {
    navigate(`/checkout/order-pay/${order.order_id}/?order_key=${order.order_key}`, {
      state: order,
    });
  };

  const handleSubmitError: SubmitErrorHandler<CheckoutForm> = () => {};

  const handleSubmitSuccess: SubmitHandler<FormSchemaType> = async (form) => {
    setIsSubmitting(true);

    try {
      await validateCart();
      const payload = await preSubmit(form);
      const order = await submitCheckoutOrder(payload);
      if (order) {
        await clearCart();
        navigateToOrderPay(order);
      }
    } catch (error) {
      handleApiError(
        error,
        appDispatch,
        "結帳發生問題，請查看錯誤並確認結帳資料是否正確，並重新再試一次",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = handleSubmit(handleSubmitSuccess, handleSubmitError);
  // #endregion submit order

  useEffect(() => {
    initializePage();
  }, [cart, shippingOptions, paymentMethods]);

  useEffect(() => {
    if (!isPageInitialized || !cvsSelectionReturn) return;
    processCvsSelectionResult(cvsSelectionReturn);
  }, [isPageInitialized, cvsSelectionReturn]);

  useEffect(() => {
    if (!shippingMethodIdField) return;
    handleShippingMethodChange();
  }, [shippingMethodIdField]);

  useEffect(() => {
    validateCartAndOrder();
  }, [cart]);

  useEffect(() => {
    const isLoading =
      !isPageInitialized ||
      isShippingMethodsLoading ||
      isCVSStoreLoading ||
      isPaymentMethodsLoading ||
      isSubmitting;
    appDispatch.setPageLoading(isLoading);
  }, [
    isPageInitialized,
    isShippingMethodsLoading,
    isCVSStoreLoading,
    isPaymentMethodsLoading,
    isSubmitting,
  ]);

  return {
    cart,

    isShippingMethodsLoading,
    shippingOptions,

    control,
    errors,
    register,
    watch,
    handleFormSubmit,
    shippingMethodIdField,

    cvsStore,
    cvsMapFormWrapRef,
    queryCVSMap,

    isPageInitialized,
    shippingMethodType,
    cvsTypeName,
    allowedPaymentMethods,
    isOrderSubmitting: isSubmitting,
    handleBeforeRedirect,
  };
};

export default useCheckoutPage;
